/**
 * MultiZoneVolumePanel — Master volume + per-zone sliders.
 *
 * Opens as a popover above the transport bar when multi-zone is active.
 * Master = average of all zone actual volumes.
 * Per-zone sliders show the actual device volume (0-100).
 */

import React, { useCallback } from 'react';
import { useTranslation } from '../i18n/useTranslation';

const GOLD = '#C9A84C';

function ZoneSlider({ zone, onChange }) {
  const handleChange = useCallback((e) => {
    onChange(zone.zoneId, parseInt(e.target.value));
  }, [zone.zoneId, onChange]);

  const pct = zone.actualVolume;

  return (
    <div className="px-1 py-1.5">
      <div className="flex items-center justify-between mb-1">
        <span
          className="text-[11px] font-medium truncate"
          style={{ color: 'var(--text-secondary)', maxWidth: '70%' }}
        >
          {zone.zoneName}
        </span>
        <span
          className="text-[11px] tabular-nums font-bold"
          style={{ color: 'var(--text-primary)', minWidth: 28, textAlign: 'right' }}
        >
          {pct}
        </span>
      </div>
      <div className="relative" style={{ height: 6 }}>
        <div
          style={{
            position: 'absolute',
            inset: 0,
            borderRadius: 3,
            background: 'var(--border)',
          }}
        />
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            bottom: 0,
            width: `${pct}%`,
            borderRadius: 3,
            background: 'var(--text-secondary)',
            transition: 'width 0.1s ease',
          }}
        />
        <input
          type="range"
          min="0"
          max="100"
          value={pct}
          onChange={handleChange}
          className="absolute inset-0 w-full opacity-0 cursor-pointer"
          style={{ height: 24, marginTop: -9 }}
          aria-label={`${zone.zoneName} volume`}
        />
      </div>
    </div>
  );
}

export default function MultiZoneVolumePanel({
  masterVolume,
  groupedZones,
  onMasterVolumeChange,
  onZoneVolumeChange,
  isOpen,
  onToggle,
}) {
  const { t } = useTranslation();

  const handleMasterChange = useCallback((e) => {
    onMasterVolumeChange(parseInt(e.target.value));
  }, [onMasterVolumeChange]);

  return (
    <div className="relative flex items-center">
      {/* Trigger: speaker icon + "Multi" label */}
      <button
        onClick={onToggle}
        className="flex items-center gap-1.5 transition hover:opacity-80"
        style={{ color: 'var(--text-secondary)' }}
        aria-label="Volume"
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
          <path d="M3 9V15H7L12 20V4L7 9H3Z" />
          {masterVolume > 0 && (
            <path d="M14 11.5C14 10.67 13.55 9.96 12.88 9.58V13.42C13.55 13.04 14 12.33 14 11.5Z" />
          )}
          {masterVolume > 50 && (
            <path d="M16.54 8.46C17.48 9.4 18 10.67 18 12C18 13.33 17.48 14.6 16.54 15.54L15.12 14.12C15.73 13.51 16.13 12.69 16.13 11.77C16.13 10.85 15.73 10.03 15.12 9.42L16.54 8.46Z" />
          )}
        </svg>
        <span className="text-[11px] font-medium" style={{ color: GOLD }}>
          {t('player.multi_label') || 'Multi'}
        </span>
      </button>

      {/* Volume panel popover */}
      {isOpen && (
        <>
          <div className="fixed inset-0 z-[51]" onClick={onToggle} />
          <div
            className="absolute bottom-full right-0 mb-2 z-[52] rounded-xl p-3 shadow-lg"
            style={{
              background: 'var(--bg-card)',
              border: '1px solid var(--border)',
              width: 280,
            }}
          >
            {/* Master slider */}
            <div className="mb-3 px-1">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[11px] font-bold uppercase tracking-wider" style={{ color: GOLD }}>
                  {t('player.master')}
                </span>
                <span
                  className="text-[11px] tabular-nums font-bold"
                  style={{ color: 'var(--text-primary)', minWidth: 28, textAlign: 'right' }}
                >
                  {masterVolume}
                </span>
              </div>
              <div className="relative" style={{ height: 6 }}>
                <div style={{ position: 'absolute', inset: 0, borderRadius: 3, background: 'var(--border)' }} />
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    bottom: 0,
                    width: `${masterVolume}%`,
                    borderRadius: 3,
                    background: GOLD,
                    transition: 'width 0.1s ease',
                  }}
                />
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={masterVolume}
                  onChange={handleMasterChange}
                  className="absolute inset-0 w-full opacity-0 cursor-pointer"
                  style={{ height: 24, marginTop: -9 }}
                  aria-label="Master Volume"
                />
              </div>
            </div>

            {/* Divider */}
            <div style={{ height: 1, background: 'var(--border)', margin: '0 4px 8px' }} />

            {/* Per-zone sliders */}
            <div className="flex flex-col gap-0.5">
              {groupedZones.map((zone) => (
                <ZoneSlider
                  key={zone.zoneId}
                  zone={zone}
                  onChange={onZoneVolumeChange}
                />
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
